import { createFileRoute, Link } from "@tanstack/react-router";
import { Award, Car, Download, FileText, MapPin } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useDriversDetailed } from "@/hooks/use-drivers";
import type { DriverDetailedItem, DriverRecentSetup } from "@/lib/types";

export const Route = createFileRoute("/_auth/app/drivers/")({
  component: DriversPage,
});

function DriverSectionSkeleton() {
  return (
    <div className="flex flex-col gap-6 py-10 animate-pulse md:flex-row md:gap-10">
      <div className="h-80 w-48 shrink-0 rounded-xl bg-muted" />
      <div className="flex-1 space-y-4">
        <div className="h-8 w-56 rounded bg-muted" />
        <div className="h-4 w-full max-w-md rounded bg-muted" />
        <div className="flex gap-2">
          <div className="h-6 w-24 rounded bg-muted" />
          <div className="h-6 w-24 rounded bg-muted" />
        </div>
        <div className="flex gap-4">
          <div className="h-10 w-20 rounded bg-muted" />
          <div className="h-10 w-20 rounded bg-muted" />
        </div>
      </div>
    </div>
  );
}

function RecentSetupChip({ setup }: { setup: DriverRecentSetup }) {
  return (
    <Link
      to="/app/setups/$setupId"
      params={{ setupId: setup.id }}
      className="group flex items-center gap-3 rounded-lg border border-border/50 bg-card/30 px-3 py-2 transition-all hover:border-folk-blue/30 hover:bg-card/60"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground group-hover:text-primary truncate transition-colors">
          {setup.title}
        </p>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1 truncate">
            <Car className="h-3 w-3 shrink-0" />
            {setup.car_name}
          </span>
          <span className="flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3 shrink-0" />
            {setup.track_name}
          </span>
        </div>
      </div>
    </Link>
  );
}

function DriverSection({ driver, index }: { driver: DriverDetailedItem; index: number }) {
  const isReversed = index % 2 === 1;

  const initials = driver.display_name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className={`flex flex-col gap-6 py-10 md:gap-8 ${
        isReversed ? "md:flex-row-reverse" : "md:flex-row"
      } md:items-stretch`}
    >
      {/* Driver photo — stretches to match content height */}
      <div className="shrink-0 md:w-52">
        {driver.avatar_url ? (
          <div className="mx-auto h-80 w-full max-w-[200px] overflow-hidden rounded-xl border-2 border-border/50 shadow-lg md:mx-0 md:h-full md:max-w-none">
            <img
              src={driver.avatar_url}
              alt={driver.display_name}
              className="h-full w-full object-cover object-[center_15%]"
            />
          </div>
        ) : (
          <div className="mx-auto flex h-80 w-full max-w-[200px] items-center justify-center rounded-xl border-2 border-border/50 bg-muted md:mx-0 md:h-full md:max-w-none">
            <span className="text-4xl font-bold text-muted-foreground">{initials}</span>
          </div>
        )}
      </div>

      {/* Driver info */}
      <div className="min-w-0 flex-1">
        {/* Name */}
        <h2
          className="text-3xl font-bold uppercase tracking-tight text-foreground md:text-4xl"
          style={{ fontFamily: "'Bebas Neue', sans-serif" }}
        >
          {driver.display_name}
        </h2>

        {/* iRating + License badges */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {driver.irating_road != null && (
            <Badge variant="secondary" className="gap-1 font-mono text-xs">
              {driver.irating_road.toLocaleString()} iR Road
            </Badge>
          )}
          {driver.irating_oval != null && driver.irating_oval > 2000 && (
            <Badge variant="secondary" className="gap-1 font-mono text-xs">
              {driver.irating_oval.toLocaleString()} iR Oval
            </Badge>
          )}
          {driver.license_road && (
            <Badge variant="outline" className="text-xs">
              {driver.license_road}
            </Badge>
          )}
        </div>

        {/* Bio */}
        {driver.bio && (
          <p className="mt-3 max-w-xl text-sm text-muted-foreground leading-relaxed">
            {driver.bio}
          </p>
        )}

        {/* Achievements (top 3) */}
        {driver.achievements && driver.achievements.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {driver.achievements.slice(0, 3).map((a: any) => (
              <div
                key={a.title}
                className="flex items-center gap-1.5 rounded-lg border border-folk-orange/20 bg-folk-orange/5 px-2.5 py-1 text-xs"
              >
                <Award className="h-3 w-3 text-folk-orange" />
                <span className="text-foreground">{a.title}</span>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="mt-4 flex items-center gap-6">
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-folk-blue" />
            <span className="font-semibold text-foreground">{driver.stats.total_setups}</span>
            <span className="text-muted-foreground">
              {driver.stats.total_setups === 1 ? "setup" : "setups"}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Download className="h-4 w-4 text-folk-green" />
            <span className="font-semibold text-foreground">
              {driver.stats.total_downloads.toLocaleString()}
            </span>
            <span className="text-muted-foreground">downloads</span>
          </div>
        </div>

        {/* Recent setups */}
        {driver.recent_setups.length > 0 && (
          <div className="mt-5">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Latest Setups
            </h4>
            <div className="flex flex-col gap-2">
              {driver.recent_setups.map((setup) => (
                <RecentSetupChip key={setup.id} setup={setup} />
              ))}
            </div>
          </div>
        )}

        {/* View profile link */}
        <Link
          to="/drivers/$driverId"
          params={{ driverId: driver.id }}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors hover:underline"
        >
          View Full Profile
        </Link>
      </div>
    </div>
  );
}

function DriversPage() {
  const { data: drivers, isLoading } = useDriversDetailed();

  return (
    <div>
      <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-foreground md:text-4xl">
        Our Drivers
      </h1>
      <p className="mt-2 max-w-lg text-muted-foreground">
        Meet the team behind our professional iRacing setups.
      </p>

      <div className="mt-6">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                <DriverSectionSkeleton />
                {i < 2 && <Separator className="opacity-30" />}
              </div>
            ))
          : (drivers ?? []).map((driver, i) => (
              <div key={driver.id}>
                {i > 0 && <Separator className="opacity-30" />}
                <DriverSection driver={driver} index={i} />
              </div>
            ))}
      </div>

      {!isLoading && (drivers ?? []).length === 0 && (
        <div className="mt-12 text-center text-muted-foreground">No drivers found.</div>
      )}
    </div>
  );
}
